import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { deviceService } from '@/services/device.service'
import type { DeviceFilters, DeviceCreateDto } from '@/types/device.types'

export const useDevices = (filters?: DeviceFilters) => {
  return useQuery({
    queryKey: ['devices', filters],
    queryFn: () => deviceService.getDevices(filters),
    staleTime: 30000,
    refetchInterval: 60000
  })
}

export const useDevice = (deviceId: string) => {
  return useQuery({
    queryKey: ['device', deviceId],
    queryFn: () => deviceService.getDevice(deviceId),
    enabled: !!deviceId
  })
}

export const useCreateDevice = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (device: DeviceCreateDto) => deviceService.createDevice(device),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] })
    }
  })
}

export const useUpdateDevice = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ deviceId, device }: { deviceId: string; device: Partial<DeviceCreateDto> }) =>
      deviceService.updateDevice(deviceId, device),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['devices'] })
      queryClient.invalidateQueries({ queryKey: ['device', variables.deviceId] })
    }
  })
}

export const useDeleteDevice = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (deviceId: string) => deviceService.deleteDevice(deviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] })
    }
  })
}

export const useArchiveDevice = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (deviceId: string) => deviceService.archiveDevice(deviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] })
    }
  })
}

export const useRestoreDevice = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (deviceId: string) => deviceService.restoreDevice(deviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] })
    }
  })
}

export const useToggleFavorite = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (deviceId: string) => deviceService.toggleFavorite(deviceId),
    onSuccess: (_, deviceId) => {
      queryClient.invalidateQueries({ queryKey: ['devices'] })
      queryClient.invalidateQueries({ queryKey: ['device', deviceId] })
    }
  })
}

export const useDeviceStats = () => {
  return useQuery({
    queryKey: ['device-stats'],
    queryFn: () => deviceService.getDeviceStats(),
    staleTime: 60000
  })
}

export const useFavoriteDevices = () => {
  return useQuery({
    queryKey: ['devices', 'favorites'],
    queryFn: () => deviceService.getFavoriteDevices(),
    staleTime: 30000,
    refetchInterval: 60000
  })
}

export const useUserDevices = (userId: string) => {
  return useQuery({
    queryKey: ['user-devices', userId],
    queryFn: () => deviceService.getUserDevices(userId),
    enabled: !!userId
  })
}
